import React from 'react';
import { useNavigate } from 'react-router-dom';

const p = 'text-[14px] text-[#8c8c8c] leading-[1.6]';
const h2 = 'text-[18px] font-medium text-black mb-3 mt-8 first:mt-0';
const block = 'space-y-3';

const CONTACT_EMAIL = 'help@bookforpaws.com';
const REGISTERED_ADDRESS = 'WEWORK, NESCO, Building 4, North Wing, NESCO IT Park, Western Express Hwy, NESCO, Goregaon East, Mumbai, Maharashtra 400063';

const ReturnRefundCancellation = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <div className="bg-[#FAF7F2] md:px-[120px] p-[20px] md:pt-[60px] md:pb-[20px] gap-[16px] flex items-center">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="hover:bg-gray-100 rounded-full shrink-0"
          aria-label="Go back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <g clipPath="url(#clip0_rrc_back)">
              <path d="M5 12H19" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 12L11 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 12L11 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <defs>
              <clipPath id="clip0_rrc_back">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
        <h1 className="text-[20px] font-medium text-black tracking-[-1px] leading-tight">
          Return, Refund and Cancellation Policy
        </h1>
      </div>

      <div className="md:px-[120px] p-[20px] md:py-[20px] flex flex-col gap-[8px] pb-10">
        <div className="bg-white rounded-[16px] p-[24px] shadow-sm">
          <p className="text-[12px] uppercase tracking-wide text-[#8c8c8c] mb-1">BookForPaws Platform</p>
          <p className="text-[14px] text-[#8c8c8c] mb-6">Last updated: 7 April 2026</p>

          <div className="space-y-3">
            <p className={p}>
              This Return, Refund and Cancellation Policy governs the cancellation, refund and related terms
              applicable to bookings made through{' '}
              <a
                href="https://www.bookforpaws.com"
                className="text-[#FF5E4C] underline underline-offset-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.bookforpaws.com
              </a>{' '}
              and the associated mobile application or platform operated by Joey &amp; Chandler Tech Ventures LLP under
              the brand BookForPaws (&quot;Platform&quot;, &quot;BFP&quot;, &quot;we&quot;, &quot;us&quot; or
              &quot;our&quot;).
            </p>
            <p className={p}>
              By making a booking through the Platform, users agree to the terms of this policy.
            </p>
          </div>

          <div className="mt-6 space-y-8">
            <section>
              <h2 className={h2}>Nature of Services</h2>
              <div className={block}>
                <p className={p}>
                  BookForPaws operates an online technology platform that enables users to discover and book
                  pet-friendly accommodations, events, training programs and other pet-related services offered by
                  independent third-party vendors. The Platform does not own, manage or operate the accommodations,
                  events or services listed on the Platform. All such services are provided by independent vendors.
                  Accordingly, cancellation and refund terms may vary depending on the policies of the respective
                  vendor.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Cancellation by Users</h2>
              <div className={block}>
                <p className={p}>
                  Users may cancel bookings in accordance with the cancellation policy specified by the vendor on the
                  relevant listing at the time of booking. Users are responsible for reviewing the applicable
                  cancellation terms before completing a booking. Where cancellation is permitted under the vendor&apos;s
                  policy, refunds, if applicable, shall be processed subject to the conditions and timelines specified by
                  the vendor. Once a booking becomes non-cancellable under the applicable vendor policy, no refund shall
                  be payable unless otherwise determined by the vendor.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Cancellation of Events</h2>
              <div className={block}>
                <p className={p}>
                  For bookings relating to events, training programs, workshops or other pet-related activities,
                  cancellation policies shall be determined by the respective event organiser or vendor. If an event
                  booking is cancelled by the user within the cancellation window permitted by the vendor, the refund
                  shall be processed in accordance with the vendor&apos;s refund policy. If the event booking is
                  cancelled after the permitted cancellation period, the booking amount may be non-refundable.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Cancellation of Pet-Friendly Stay Bookings</h2>
              <div className={block}>
                <p className={p}>
                  For bookings relating to pet-friendly accommodations including hotels, resorts, homestays, villas or
                  other stays, cancellation terms shall be governed by the policy specified on the property listing.
                  Users are advised to carefully review the cancellation terms, including refund eligibility, before
                  confirming their booking. Some accommodations may offer flexible cancellation policies, while others
                  may operate under partial or non-refundable booking terms.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Cancellation by Vendors</h2>
              <div className={block}>
                <p className={p}>
                  In certain circumstances, a vendor may cancel a booking due to reasons including but not limited to
                  property unavailability, operational constraints, safety concerns, force majeure events or other
                  unforeseen circumstances. Where a vendor cancels a confirmed booking, the vendor shall be responsible
                  for refunding the applicable booking amount to the user in accordance with the applicable policy.
                  Where payments were processed through the Platform, the Platform may facilitate the refund process on
                  behalf of the vendor. The Platform shall not be financially liable for vendor-initiated
                  cancellations.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Refund Processing</h2>
              <div className={block}>
                <p className={p}>
                  Where a refund is applicable, the refund shall ordinarily be processed to the original method of
                  payment used at the time of booking. Refund timelines may vary depending on the payment gateway
                  provider, banking institutions and the policies of the user&apos;s card issuer or payment provider.
                  The Platform shall not be responsible for delays in refund processing caused by banks, payment
                  gateways or other financial institutions.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Platform Fees and Charges</h2>
              <div className={block}>
                <p className={p}>
                  Service fees, convenience fees, payment gateway charges or other platform-related charges may be
                  non-refundable unless otherwise specified. In cases where refunds are processed, such fees may be
                  deducted where applicable.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>No Show Policy</h2>
              <div className={block}>
                <p className={p}>
                  If a user fails to attend the event or check in at the accommodation at the scheduled time without
                  prior cancellation in accordance with the applicable policy, the booking may be treated as a
                  &quot;No Show&quot;. In such cases, the booking amount may be non-refundable subject to the vendor&apos;s
                  policies.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Force Majeure</h2>
              <div className={block}>
                <p className={p}>
                  Bookings may be affected by circumstances beyond the control of the Platform or vendors including
                  natural disasters, government restrictions, pandemics, travel restrictions or other force majeure
                  events. In such cases, refunds or rescheduling may be subject to the vendor&apos;s policies and
                  operational feasibility.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Platform Liability</h2>
              <div className={block}>
                <p className={p}>
                  The Platform acts solely as a facilitator for listing and booking services offered by independent
                  vendors. The Platform shall not be responsible for the quality, availability or performance of
                  services provided by vendors and shall not be liable for any losses arising from cancellations,
                  refund disputes or service issues.
                </p>
              </div>
            </section>

            <section>
              <h2 className={h2}>Contact</h2>
              <div className={`${block} mt-3`}>
                <p className={p}>
                  If you have any questions regarding cancellations or refunds, you may contact:
                </p>
                <div className="p-4 bg-[#FF5E4C] bg-opacity-10 border border-[#FF5E4C] border-opacity-20 rounded-lg">
                  <p className={`${p} font-medium text-black`}>BookForPaws</p>
                  <p className={`${p} mt-1`}>Joey &amp; Chandler Tech Ventures LLP</p>
                  <p className={`${p} mt-3`}>
                    <strong className="text-black">Email:</strong>{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF5E4C] underline underline-offset-2">
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                  <p className={`${p} mt-2`}>
                    <strong className="text-black">Address:</strong> {REGISTERED_ADDRESS}
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnRefundCancellation;
